
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { devLog, devError } from "@/utils/secureLogger";
import { secureStorage } from "@/utils/secureStorage";

export const useEmailVerification = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState("");
  const [pendingCredentials, setPendingCredentials] = useState<{email: string, password: string, username: string} | null>(null);
  const { verifyOtp, resendVerificationEmail } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    // Check for stored email on initial load
    const storedEmail = secureStorage.get<string>('pendingVerificationEmail');
    if (storedEmail) {
      setPendingVerificationEmail(storedEmail);
      setIsVerifying(true);
      
      // Try to retrieve pending credentials from secureStorage
      const storedCredentials = secureStorage.get<{email: string, password: string, username: string}>('pendingCredentials');
      if (storedCredentials) {
        setPendingCredentials(storedCredentials);
      }
    }
  }, []);

  const handleVerifyCode = async (verificationCode: string) => {
    const { toast } = useToast();
    let isLoading = true;
    
    try {
      const verifyEmail = pendingVerificationEmail;
      
      if (!verifyEmail) {
        toast({
          title: "Error",
          description: "Email is required for verification",
          variant: "destructive",
        });
        isLoading = false;
        return { isLoading };
      }
      
      if (!verificationCode || verificationCode.length < 6) {
        toast({
          title: "Error",
          description: "Please enter a valid verification code",
          variant: "destructive",
        });
        isLoading = false;
        return { isLoading };
      }
      
      devLog("Verifying OTP for email");
      
      // Pass the pending credentials to the verifyOtp function
      const { error } = await verifyOtp(verifyEmail, verificationCode, pendingCredentials || undefined);
      if (error) throw error;
      
      // Clear stored verification data
      secureStorage.remove('pendingVerificationEmail');
      secureStorage.remove('pendingCredentials');
      setPendingCredentials(null);
      setIsVerifying(false);
      
      isLoading = false;
      return { isLoading, success: true };
    } catch (error: any) {
      devError("Verification error:", error);
      toast({
        title: "Verification error",
        description: error.message || "An error occurred during verification",
        variant: "destructive",
      });
      isLoading = false;
      return { isLoading, success: false, error };
    }
  };

  const handleResendCode = async () => {
    let isLoading = true;
    
    try {
      const resendEmail = pendingVerificationEmail;
      
      if (!resendEmail) {
        toast({
          title: "Error",
          description: "Email is required to resend verification",
          variant: "destructive",
        });
        return { isLoading: false };
      }
      
      devLog("Resending verification code");
      const { error } = await resendVerificationEmail(resendEmail);
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Verification email has been resent to your email",
      });
      
      return { isLoading: false, success: true };
    } catch (error: any) {
      devError("Error resending code:", error);
      toast({
        title: "Error resending code",
        description: error.message || "An error occurred while resending the verification code",
        variant: "destructive",
      });
      return { isLoading: false, success: false, error };
    }
  };

  const resetVerification = () => {
    setIsVerifying(false);
    setPendingVerificationEmail("");
    secureStorage.remove('pendingVerificationEmail');
    secureStorage.remove('pendingCredentials');
    setPendingCredentials(null);
  };

  return {
    isVerifying,
    setIsVerifying,
    pendingVerificationEmail,
    setPendingVerificationEmail,
    pendingCredentials,
    setPendingCredentials,
    handleVerifyCode,
    handleResendCode,
    resetVerification
  };
};
